/**
 * Reel — plays the teaser script (docs/teaser-script.md) inside the running game and records it.
 *
 * NOT a cinematic system, and deliberately not one. IT DOES NOT TOUCH THE CAMERA. The rig that
 * films this is the same top-down follow rig you play through, ventilation-driven pull-back and
 * all — because a trailer shot from a camera the game does not have is a promise the game does
 * not keep. What the reel drives is where the shark IS, who is swimming with it, and three
 * cards. Everything else is the game doing what it always does.
 *
 * FIVE BEATS IN THIRTY SECONDS, and the length is the point. An early cut ran nine, and a
 * three-second average is a pace that argues with the thing it is selling: a game with no score
 * and no clock cannot be sold by a trailer that behaves like one. Six seconds is long enough
 * that a viewer stops waiting for the next cut and starts looking at the water, which is the
 * only state in which any of this is persuasive.
 *
 * THE CLOCK IS FIXED-STEP, NOT WALL TIME. The reel advances by exactly 1/REEL_FPS per drawn
 * frame regardless of how long the frame took, so a shark meshing on a worker or a kelp replant
 * costs nothing in the output — the timeline just takes longer in the room. Recording against
 * wall time would leave every hitch in the file, and the hitches are the frames a viewer sees.
 *
 * The camera is driven in world space rather than through GameCamera, because that rig exists
 * to follow a player and this needs to be somewhere the player is not.
 */
import { DAY_SECONDS } from '../world/DayNight.ts';
import { REEL_FPS } from './Recorder.ts';
import { CardLayer, LINE_BREAK } from './CardLayer.ts';

export type ReelFormat = '9x16' | '1x1' | '16x9';

export const FORMATS: Record<ReelFormat, { w: number; h: number }> = {
  '9x16': { w: 1080, h: 1920 },
  '1x1': { w: 1080, h: 1080 },
  '16x9': { w: 1920, h: 1080 },
};

export function parseFormat(raw: string | null): ReelFormat | null {
  if (raw === null) return null;
  const k = raw.trim().toLowerCase().replace(/[:_]/g, 'x');
  if (k === '' || k === '1') return '9x16';
  return k === '9x16' || k === '1x1' || k === '16x9' ? k : '9x16';
}

/** what the world has to do for a shot; every field is optional and holds its last value */
interface Shot {
  /** seconds from the start of the reel */
  at: number;
  label: string;
  /** day phase to hold, 0..1. Omitted keeps the sky running at whatever rate is set. */
  phase?: number;
  /** how fast the sky runs during this shot, in days per second of reel */
  skyRate?: number;
  /** cut the shark to a place worth filming; a beat boundary is a cut, so this is a hard move */
  place?: { x: number; z: number; heading: number };
  /** how many companions should be swimming alongside by now */
  friends?: number;
  /** a title card, or '' to clear one */
  card?: string;
  cardSub?: string;
}

/* The shot list, straight out of docs/teaser-script.md. Timings there are authoritative; if the
   two disagree the document is right and this is stale. */
const SHOTS: Shot[] = [
  /* ONE CONTINUOUS DAY. The sky is set running at dawn and never touched again — no pinning,
     no per-shot phase. That single unbroken sunrise-to-night IS the story the piece tells, and
     the moment you snap the sky between shots it stops being a day and becomes a set of
     lighting presets.

     0.245 rather than a truer dawn: at 0.22 the opening frame measures 70% night, which is
     atmospheric on a monitor and unreadable on a phone at arm's length in daylight — and the
     first second is the one that cannot afford to be squinted at. 0.245 is 05:52, still a sun
     low enough to rake, at 47% night. 0.025 days a second then puts peak light on the reef beat
     and 90% night on the glow. */
  { at: 0.0, label: 'first light, alone', phase: 0.245, skyRate: 0.025, friends: 0, card: '',
    place: { x: -10, z: 30, heading: 0.5 } },

  { at: 3.0, label: 'card: your shore', card: `THIS IS${LINE_BREAK}YOUR SHORE` },
  { at: 6.2, label: 'card out', card: '' },

  // Beat two. The kelp, and the longest single hold in the piece.
  { at: 7.0, label: 'into the kelp', place: { x: -46, z: 35, heading: 0.30 } },

  // Beat three. Out to the reef and over the edge, at the top of the day.
  { at: 13.5, label: 'the reef and the edge', place: { x: 74, z: 96, heading: 0.18 } },

  // Beat four. Company, at the exact moment the light starts to go.
  { at: 19.0, label: 'company', friends: 2, place: { x: 10, z: 88, heading: -1.35 } },
  { at: 20.6, label: 'card: not alone', card: `YOU DON’T HAVE TO${LINE_BREAK}SWIM IT ALONE` },
  { at: 22.0, label: 'a third', friends: 3 },
  { at: 24.4, label: 'card out', card: '' },

  // Beat five. Back to where it started, in the dark, lit from below.
  { at: 25.2, label: 'bioluminescence', place: { x: -34, z: 34, heading: 1.9 } },

  { at: 27.6, label: 'end card', card: 'SHOREPLAY.APP', cardSub: 'DIVE IN' },
  { at: 30.0, label: 'end' },
];

export const REEL_SECONDS = SHOTS[SHOTS.length - 1].at;

export interface ReelWorld {
  /** pin the sky, or let it run */
  setSkyPhase(phase: number): void;
  setSkyRate(rate: number): void;
  /** how many scripted companions swim with the player */
  setCompanions(n: number): void;
  /** cut the shark to a location; the game's own camera reacquires */
  place(x: number, z: number, heading: number): void;
  /** true once the reef and the shark have finished building */
  ready(): boolean;
}

export class Reel {
  readonly format: ReelFormat;
  /** seconds of TIMELINE elapsed — not wall clock; see the note at the top */
  private t = -1.2;
  private shot = -1;
  /** cards are drawn into the canvas, not the DOM — see CardLayer for why */
  readonly cards: CardLayer;

  constructor(format: ReelFormat) {
    this.format = format;
    this.cards = new CardLayer(FORMATS[format].w, FORMATS[format].h);
  }

  get done(): boolean {
    return this.t >= REEL_SECONDS;
  }

  /** 0..1 through the timeline, for a progress readout */
  get progress(): number {
    return Math.max(0, Math.min(1, this.t / REEL_SECONDS));
  }

  get shotLabel(): string {
    return this.shot >= 0 ? SHOTS[this.shot].label : 'waiting for the world';
  }

  /**
   * Advance one captured frame.
   *
   * Returns false while the world is still building — the caller must not record those frames
   * or the reel opens on a half-streamed reef.
   */
  step(world: ReelWorld): boolean {
    if (!world.ready()) return false;
    // A beat of lead-in so the first shot starts on a settled world rather than on frame one.
    this.t += 1 / REEL_FPS;
    if (this.t < 0) return false;

    let i = 0;
    while (i < SHOTS.length - 1 && SHOTS[i + 1].at <= this.t) i++;
    if (i !== this.shot) {
      this.shot = i;
      this.enter(SHOTS[i], world);
    }
    return true;
  }

  dispose(): void {
    this.cards.dispose();
  }

  private enter(shot: Shot, world: ReelWorld): void {
    if (shot.place) world.place(shot.place.x, shot.place.z, shot.place.heading);
    if (shot.phase !== undefined) world.setSkyPhase(shot.phase);
    if (shot.skyRate !== undefined) {
      // Rate is in days per second of reel; the sky wants seconds of sim per second.
      world.setSkyRate(shot.skyRate * DAY_SECONDS);
    }
    if (shot.friends !== undefined) world.setCompanions(shot.friends);
    if (shot.card !== undefined) this.showCard(shot.card, shot.cardSub ?? '');
  }

  private showCard(title: string, sub: string): void {
    this.cards.show(title, sub);
  }
}
